// LUT par canal pour le viewer Viv : LUT Fiji (Fire et celles du dossier luts/ de Fiji), extension de shader
// « une LUT par canal », utilitaires d'affichage (couleur d'une LUT, histogramme). Repris du prototype
// prototypes/viv-viewer.
import { VivLayerExtension } from '@hms-dbmi/viv';

import { FIJI_LUTS } from './fijiLuts';

// LUT Fire d'ImageJ/Fiji (ij.plugin.LutLoader.fire) : 32 points de contrôle, interpolés linéairement sur 256 niveaux.
export const FIRE = {
  r: [0,0,1,25,49,73,98,122,146,162,173,184,195,207,217,229,240,252,255,255,255,255,255,255,255,255,255,255,255,255,255,255],
  g: [0,0,0,0,0,0,0,0,0,0,0,0,0,14,35,57,79,101,117,133,147,161,175,190,205,219,234,248,255,255,255,255],
  b: [0,61,96,130,165,192,220,227,210,181,151,122,93,64,35,5,0,0,0,0,0,0,0,0,0,35,98,160,223,255,255,255],
};
const decode = b64 => {
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  return { r: Array.from(bytes.subarray(0, 256)), g: Array.from(bytes.subarray(256, 512)), b: Array.from(bytes.subarray(512)) };
};
const FIJI = Object.fromEntries(Object.entries(FIJI_LUTS).map(([id, b64]) => [id, decode(b64)]));

// Red Hot adoucie (demande du 2026-10-21) : la version de Fiji passe au blanc sur le dernier tiers (quasi blanche dès
// 228 / 255). Ici le rouge puis le jaune s'étirent un peu, le bleu ne commence qu'à 62 % et monte en douceur
// (puissance 1,5) jusqu'à 55 % : fin sur un jaune pâle (255, 250, 140), jamais blanche ; luminance toujours croissante.
// L'originale reste proposée sous le nom « Red Hot (Fiji) ».
const clamp01 = v => Math.min(1, Math.max(0, v));
const SOFT_RED_HOT = ['r', 'g', 'b'].reduce((lut, k) => ({ ...lut, [k]: Array.from({ length: 256 }, (_, i) => {
  const t = i / 255;
  const c = { r: clamp01(t / 0.36), g: clamp01((t - 0.30) / 0.55) * 0.98, b: clamp01((t - 0.62) / 0.38) ** 1.5 * 0.55 }[k];
  return Math.round(255 * c);
}) }), {});

// RdBu de ColorBrewer, inversée (bleu = bas, rouge = haut), interpolée sur 256 niveaux : palette divergente pour des
// valeurs signées (contributions d'un modèle), 0 au milieu en blanc.
const RDBU_HEX = ['053061', '2166ac', '4393c3', '92c5de', 'd1e5f0', 'f7f7f7', 'fddbc7', 'f4a582', 'd6604d', 'b2182b', '67001f'];
const RDBU = ['r', 'g', 'b'].reduce((lut, k, ki) => ({ ...lut, [k]: Array.from({ length: 256 }, (_, i) => {
  const x = (i / 255) * (RDBU_HEX.length - 1);
  const j = Math.min(Math.floor(x), RDBU_HEX.length - 2);
  const c = n => parseInt(RDBU_HEX[n].slice(2 * ki, 2 * ki + 2), 16);
  return Math.round(c(j) + (x - j) * (c(j + 1) - c(j)));
}) }), {});

// Hématoxyline (demande du 2026-11-03) : pour une transmission (verre clair) affichée comme une lame colorée. Loi de
// Beer-Lambert sur la transmittance t (contraste [noir, verre]) : couleur = t^(β × 1,5), avec les coefficients
// d'absorption RGB de l'hématoxyline du HE virtuel (BETA_H de virtualHe.js) : blanc sur le verre, violet-bleu
// (104, 90, 187) à t = 0,5, bleu nuit pour les zones les plus absorbantes.
const HEMATOXYLIN = ['r', 'g', 'b'].reduce((lut, k, ki) => ({ ...lut, [k]: Array.from({ length: 256 }, (_, i) =>
  Math.round(255 * (i / 255) ** ([0.86, 1.0, 0.3][ki] * 1.5))) }), {});

export const LUTS = {
  fire: FIRE, ...FIJI, 'Red Hot': SOFT_RED_HOT, 'Red Hot (Fiji)': FIJI['Red Hot'], RdBu: RDBU, Hematoxylin: HEMATOXYLIN,
};

// LUT proposées, de la plus adaptée à la moins adaptée à nos images (fluorescence et diffusion sans marquage : fond
// noir, grande dynamique, comparées au HE rose / violet) :
// - recommandées : la luminance croît avec l'intensité et part du noir (le fond reste noir, l'intensité se lit) ;
//   d'abord les palettes perceptuellement uniformes (mpl-inferno, mpl-magma : pas de faux contours), puis Fire
//   (classique, très contrastée mais à paliers), les « Hot » d'une seule teinte (Orange et Cyan tranchent sur le HE),
//   et d'autres rampes croissantes ;
// - réglage du contraste : HiLo et glow signalent en couleur les pixels saturés au minimum et au maximum ;
// - autres palettes continues : luminance non monotone ou fond non noir (mpl-viridis et mpl-plasma partent du violet) ;
// - divergentes : valeurs signées (0 au milieu) ;
// - colorations : transmissions sur fond clair, rendues comme une lame colorée (hématoxyline) ;
// - étiquettes, segmentation : couleurs distinctes sans ordre, déconseillées pour des intensités.
export const LUT_GROUPS = [
  { label: 'Recommended', ids: ['mpl-inferno', 'mpl-magma', 'fire', 'Orange Hot', 'Cyan Hot', 'Yellow Hot', 'Green Fire Blue',
    'Magenta Hot', 'Red Hot', 'thallium', 'gem', 'sepia', 'blue_orange_icb'] },
  { label: 'Contrast adjustment (saturation shown)', ids: ['HiLo', 'glow'] },
  { label: 'Other continuous palettes', ids: ['mpl-viridis', 'mpl-plasma', 'Red Hot (Fiji)', 'Thermal', 'physics', 'royal',
    'smart', 'thal', 'cool', 'phase', 'Rainbow RGB'] },
  { label: 'Diverging (signed values)', ids: ['RdBu'] },
  { label: 'Stains (transmission, light background)', ids: ['Hematoxylin'] },
  { label: 'Labels, segmentation (not advised for intensities)', ids: ['glasbey_on_dark', 'glasbey',
    'glasbey_inverted', '16_colors', '5_ramps', '6_shades', 'brgbcmyw', 'unionjack', 'ICA', 'ICA2', 'ICA3', 'edges'] },
].map(group => ({ ...group, ids: group.ids.filter(id => LUTS[id]) }));
// LUT du dossier Fiji absentes du classement (ajoutées depuis) : à la fin.
const ranked = new Set(LUT_GROUPS.flatMap(g => g.ids));
const unranked = Object.keys(LUTS).filter(id => !ranked.has(id));
if (unranked.length) LUT_GROUPS.push({ label: 'Others', ids: unranked });
export const LUT_IDS = ['color', ...LUT_GROUPS.flatMap(g => g.ids)];

// Extension Viv « une LUT par canal ». Calquée sur ColorPaletteExtension de Viv 0.23 : les lignes contenant
// VIV_CHANNEL_INDEX sont dupliquées par Viv pour chaque canal. Le shader ne contient que les LUT des canaux de la
// couche (tables de 256 couleurs : les inclure toutes alourdirait chaque compilation) : un module par jeu de LUT.
const IDX = 'VIV_CHANNEL_INDEX';
const glslLutFn = (fn, lut) => {
  const n = lut.r.length;
  const pts = lut.r.map((_, i) => `vec3(${[lut.r[i], lut.g[i], lut.b[i]].map(v => (v / 255).toFixed(3)).join(',')})`);
  return `
vec3 ${fn}(float t) {
  const vec3 P[${n}] = vec3[${n}](${pts.join(',')});
  float x = clamp(t, 0., 1.) * ${(255 * n / 256).toFixed(4)};  // même échelle que l'interpolation d'ImageJ
  int i = int(floor(x));
  return mix(P[i], P[min(i + 1, ${n - 1})], x - float(i));
}`;
};
const glslLut = (k, lut) => glslLutFn(`lut_${k}`, lut);
// Fonction GLSL vec3 fn(float t) d'une LUT (Fire si l'identifiant est inconnu), pour d'autres modules de shader.
export const lutGlsl = (fn, lutId) => glslLutFn(fn, LUTS[lutId] || FIRE);
const modules = new Map();
// Module pour une liste de LUT (une par canal) ; nom propre au jeu de LUT, repris dans updateState.
function lutModuleFor(luts) {
  const used = [...new Set(luts.filter(id => LUTS[id]))].sort();
  const key = used.join('|');
  if (modules.has(key)) return modules.get(key);
  const name = `lutModule${modules.size}`;
  const module = {
    name,
    used,
    uniformTypes: {
      opacity: 'f32', [`color${IDX}`]: 'vec3<f32>', [`lut${IDX}`]: 'i32', [`on${IDX}`]: 'f32', [`inv${IDX}`]: 'f32',
      [`lo${IDX}`]: 'f32', [`hi${IDX}`]: 'f32',
    },
    fs: `
uniform ${name}Uniforms {
  float opacity;
  vec3 color${IDX};
  int lut${IDX};
  float on${IDX};
  float inv${IDX};
  float lo${IDX};
  float hi${IDX};
} ${name};
${used.map((id, k) => glslLut(k, LUTS[id])).join('\n')}

void mutate_color(inout vec3 rgb, float[NUM_CHANNELS] intensity) {
  vec3 colors[NUM_CHANNELS] = vec3[NUM_CHANNELS](
${name}.color${IDX},
  );
  int luts[NUM_CHANNELS] = int[NUM_CHANNELS](
${name}.lut${IDX},
  );
  float on[NUM_CHANNELS] = float[NUM_CHANNELS](
${name}.on${IDX},
  );
  float inv[NUM_CHANNELS] = float[NUM_CHANNELS](
${name}.inv${IDX},
  );
  float los[NUM_CHANNELS] = float[NUM_CHANNELS](
${name}.lo${IDX},
  );
  float his[NUM_CHANNELS] = float[NUM_CHANNELS](
${name}.hi${IDX},
  );
  for (int i = 0; i < NUM_CHANNELS; i++) {
float v = clamp(intensity[i], 0., 1.);
v = mix(v, 1. - v, inv[i]);  // LUT inversée
vec3 c = v * colors[i];
float t = mix(los[i], his[i], v);  // partie de la LUT utilisée
${used.map((_, k) => `    if (luts[i] == ${k + 1}) c = lut_${k}(t);`).join('\n')}
rgb += on[i] * c;
  }
}`,
    inject: { 'fs:DECKGL_MUTATE_COLOR': `vec3 rgb = rgba.rgb;\nmutate_color(rgb, intensity);\nrgba = vec4(rgb * ${name}.opacity, ${name}.opacity);` },
  };
  modules.set(key, module);
  return module;
}
const extensionOf = layer => (layer.props.extensions || []).find(e => e instanceof LutExtension);

export class LutExtension extends VivLayerExtension {
  // Appelée avec this = la couche.
  getVivShaderTemplates() { return { modules: [lutModuleFor(extensionOf(this)?.opts.luts || [])] }; }

  updateState(params, extension) {
    super.updateState(params, extension);
    const { colors = [], channelsVisible = [], opacity = 1, lutRanges = [] } = this.props;
    const { luts = [], inverted = [] } = extension.opts;
    const module = lutModuleFor(luts);
    const n = (this.props.selections || []).length;
    const uniforms = { opacity };
    for (let c = 0; c < n; c++) {
      uniforms[`color${c}`] = (colors[c] || [255, 255, 255]).map(v => v / 255);
      uniforms[`lut${c}`] = module.used.indexOf(luts[c]) + 1;  // 0 : couleur unie
      uniforms[`on${c}`] = channelsVisible[c] === false ? 0 : 1;
      uniforms[`inv${c}`] = inverted[c] ? 1 : 0;
      [uniforms[`lo${c}`], uniforms[`hi${c}`]] = lutRanges[c] || [0, 1];  // prop « lutRanges » de la couche
    }
    for (const model of this.getModels()) model.shaderInputs.setProps({ [module.name]: uniforms });
  }
}
LutExtension.extensionName = 'LutExtension';

// Couleur d'une LUT en t ∈ [0, 1], même interpolation que le shader ('color' = noir → couleur du canal). range : partie
// de la LUT utilisée [début, fin] (t ramené dedans), pour une vraie LUT seulement.
export function lutColor(lutId, color, inverted, t, range = null) {
  t = Math.min(1, Math.max(0, inverted ? 1 - t : t));
  const lut = LUTS[lutId];
  if (!lut) return color.map(v => v * t);
  if (range) t = range[0] + t * (range[1] - range[0]);
  const n = lut.r.length;
  const x = t * 255 * n / 256;
  const i = Math.floor(x);
  const j = Math.min(i + 1, n - 1);
  const f = x - i;
  return ['r', 'g', 'b'].map(k => lut[k][i] * (1 - f) + lut[k][j] * f);
}
export const css = rgb => `rgb(${rgb.map(Math.round).join(', ')})`;

export function histogram(data, [lo, hi], bins = 128) {
  const counts = new Float64Array(bins);
  const span = hi - lo || 1;
  for (let k = 0; k < data.length; k++) {
    const b = Math.min(bins - 1, Math.floor(((data[k] - lo) / span) * bins));
    if (b >= 0) counts[b]++;
  }
  return counts;
}


export const hexToRgb = hex => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
export const rgbToHex = rgb => '#' + rgb.map(v => Math.round(v).toString(16).padStart(2, '0')).join('');
export const lutLabel = id => (id === 'color' ? 'Solid color' : id === 'fire' ? 'Fire' : id);
